import Dexie, {type Table} from "dexie";
import type {FileMetadata, FileStorageAdapter} from "@/types/FileStorageInterfaces.ts";
import { v4 as uuidv4 } from 'uuid';
import { isDemoModeEnabled } from '@/demo/demo-mode.ts'

interface StoredFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  eventId: number;
  fileType: string;
  data: Uint8Array;
  checksumSha256?: string;
  cloudChecksumSha256?: string | null;
  cloudSize?: number | null;
  lastSyncedAt?: Date | null;
  createdAt: Date;
}

interface ZipManifestFileEntry {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  eventId: number;
  fileType: string;
  createdAt: string;
}

interface ZipManifest {
  exportDate: string;
  totalFiles: number;
  files: ZipManifestFileEntry[];
}

type SyncOperationType = 'upload' | 'delete';

interface SyncQueueItem {
  id: string;
  userScope: string;
  fileId: string;
  operation: SyncOperationType;
  attempts: number;
  nextAttemptAt: Date;
  lastError?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface StoredFileSyncMetadata {
  id: string;
  checksumSha256?: string;
  cloudChecksumSha256?: string | null;
  cloudSize?: number | null;
  lastSyncedAt?: Date | null;
}

class FileStorageDB extends Dexie {
  files!: Table<StoredFile>;
  syncQueue!: Table<SyncQueueItem>;

  constructor(dbName: string) {
    super(dbName);
    this.version(1).stores({
      files: 'id, filename, eventId, fileType, createdAt'
    });
    this.version(2).stores({
      files: 'id, filename, eventId, fileType, createdAt',
      syncQueue: 'id, userScope, fileId, operation, nextAttemptAt, updatedAt'
    });
  }
}

export class IndexedDBAdapter implements FileStorageAdapter {
  private db: FileStorageDB;

  constructor(dbName?: string) {
    const modeAwareDbName = dbName ?? (isDemoModeEnabled() ? 'TTFileStorageDB_demo' : 'TTFileStorageDB')
    this.db = new FileStorageDB(modeAwareDbName);
  }

  private async sha256Hex(data: Uint8Array): Promise<string | undefined> {
    if (!globalThis.crypto?.subtle) return undefined;
    const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  private syncQueueId(userScope: string, fileId: string, operation: SyncOperationType): string {
    return `${userScope}:${operation}:${fileId}`;
  }

  async store(file: File | Uint8Array, metadata: Omit<FileMetadata, 'id'>, id?: string): Promise<string> {
    const fileId = id || uuidv4();

    let data: Uint8Array;
    if (file instanceof File) {
      data = new Uint8Array(await file.arrayBuffer());
    } else {
      data = file;
    }

    const checksumSha256 = await this.sha256Hex(data);

    const storedFile: StoredFile = {
      id: fileId,
      ...metadata,
      data,
      checksumSha256,
      cloudChecksumSha256: null,
      cloudSize: null,
      lastSyncedAt: null,
      createdAt: new Date()
    };

    await this.db.files.put(storedFile);
    return fileId;
  }

  async retrieve(id: string): Promise<Blob> {
    const file = await this.db.files.get(id);
    if (!file) {
      throw new Error(`File with id ${id} not found`);
    }

    return new Blob([file.data], { type: file.mimeType });
  }

  async delete(id: string): Promise<boolean> {
    try {
      console.log(`Attempting to delete file with id: ${id}`);

      // Check if file exists
      const file = await this.db.files.get(id);
      if (!file) {
        console.warn(`File with id ${id} not found`);
        return false;
      }

      // Delete the file
      await this.db.files.delete(id);
      console.log(`Successfully deleted file with id: ${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting file from IndexedDB:', error);
      return false;
    }
  }

  async getUrl(id: string): Promise<string> {
    const blob = await this.retrieve(id);
    return URL.createObjectURL(blob);
  }

  async exists(id: string): Promise<boolean> {
    const file = await this.db.files.get(id);
    return !!file;
  }

  async clearAll(): Promise<void> {
    await this.db.files.clear();
    if (this.db.syncQueue) {
      await this.db.syncQueue.clear();
    }
  }

  // Additional methods for cleanup
  async cleanup(): Promise<void> {
    // Clean up old files if needed
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 30); // Remove files older than 30 days

    await this.db.files.where('createdAt').below(cutoffDate).delete();
  }

  async getFilesByEvent(eventId: number): Promise<StoredFile[]> {
    return await this.db.files.where('eventId').equals(eventId).toArray();
  }

  // Helper method to get file metadata (useful for syncing)
  async getFileMetadata(id: string): Promise<Omit<FileMetadata, 'data'> | null> {
    const file = await this.db.files.get(id);
    if (!file) return null;

    return {
      id: file.id,
      filename: file.filename,
      mimeType: file.mimeType,
      size: file.size,
      eventId: file.eventId,
      fileType: file.fileType as any
    };
  }

  async getAllFileSyncMetadata(): Promise<StoredFileSyncMetadata[]> {
    const files = await this.db.files.toArray();
    return files.map((file) => ({
      id: file.id,
      checksumSha256: file.checksumSha256,
      cloudChecksumSha256: file.cloudChecksumSha256,
      cloudSize: file.cloudSize,
      lastSyncedAt: file.lastSyncedAt ?? null,
    }));
  }

  async markFileSynced(
    id: string,
    cloudInfo?: { checksumSha256?: string; size?: number }
  ): Promise<void> {
    await this.db.files.update(id, {
      lastSyncedAt: new Date(),
      cloudChecksumSha256: cloudInfo?.checksumSha256 ?? null,
      cloudSize: cloudInfo?.size ?? null,
    });
  }

  async enqueueSyncOperation(userScope: string, fileId: string, operation: SyncOperationType): Promise<void> {
    const now = new Date();
    const existing = await this.db.syncQueue.get(this.syncQueueId(userScope, fileId, operation));
    if (existing) {
      await this.db.syncQueue.update(existing.id, {
        attempts: 0,
        nextAttemptAt: now,
        updatedAt: now,
        lastError: null,
      });
      return;
    }

    const item: SyncQueueItem = {
      id: this.syncQueueId(userScope, fileId, operation),
      userScope,
      fileId,
      operation,
      attempts: 0,
      nextAttemptAt: now,
      lastError: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.syncQueue.put(item);
  }

  async removeSyncOperation(userScope: string, fileId: string, operation?: SyncOperationType): Promise<void> {
    if (operation) {
      await this.db.syncQueue.delete(this.syncQueueId(userScope, fileId, operation));
      return;
    }

    await this.db.syncQueue.delete(this.syncQueueId(userScope, fileId, 'upload'));
    await this.db.syncQueue.delete(this.syncQueueId(userScope, fileId, 'delete'));
  }

  async listDueSyncOperations(userScope: string): Promise<Array<{ id: string; fileId: string; operation: SyncOperationType; attempts: number }>> {
    const now = new Date();
    const items = await this.db.syncQueue
      .where('userScope')
      .equals(userScope)
      .filter((item) => item.nextAttemptAt <= now)
      .sortBy('updatedAt');

    return items.map((item) => ({
      id: item.id,
      fileId: item.fileId,
      operation: item.operation,
      attempts: item.attempts,
    }));
  }

  async markSyncOperationFailed(syncQueueItemId: string, error: unknown, attempts: number): Promise<void> {
    const now = new Date();
    const nextDelayMs = Math.min(5 * 60 * 1000, Math.max(5_000, (2 ** attempts) * 1_000));
    await this.db.syncQueue.update(syncQueueItemId, {
      attempts,
      lastError: error instanceof Error ? error.message : String(error),
      nextAttemptAt: new Date(now.getTime() + nextDelayMs),
      updatedAt: now,
    });
  }

  /**
   * Export all files as a ZIP archive (browser-compatible)
   */
  async exportAsZip(filename: string = 'timethread-files.zip'): Promise<Blob> {
    // Dynamic import for better tree shaking
    const JSZip = (await import('jszip')).default;

    const files = await this.db.files.toArray();
    const zip = new JSZip();

    // Create manifest
    const manifest = {
      exportDate: new Date().toISOString(),
      totalFiles: files.length,
      files: files.map(f => ({
        id: f.id,
        filename: f.filename,
        mimeType: f.mimeType,
        size: f.size,
        eventId: f.eventId,
        fileType: f.fileType,
        createdAt: f.createdAt.toISOString()
      }))
    };

    // Add manifest
    zip.file('manifest.json', JSON.stringify(manifest, null, 2));

    // Add all files with proper folder structure
    for (const file of files) {
      const path = `event-${file.eventId}/${file.fileType}/${file.filename}`;
      zip.file(path, file.data, {
        date: file.createdAt
      });
    }

    return await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Import files from a ZIP archive created by exportAsZip
   */
  async importFromZip(fileOrBlob: File | Blob): Promise<{ imported: number; skipped: number; total: number }> {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(fileOrBlob);

    const manifestFile = zip.file('manifest.json');
    if (!manifestFile) {
      throw new Error('Invalid file archive: manifest.json missing');
    }

    const manifestText = await manifestFile.async('text');
    const manifest = JSON.parse(manifestText) as ZipManifest;

    if (!Array.isArray(manifest.files)) {
      throw new Error('Invalid file archive: malformed manifest');
    }

    let imported = 0;
    let skipped = 0;

    for (const item of manifest.files) {
      const zipPath = `event-${item.eventId}/${item.fileType}/${item.filename}`;
      const archivedFile = zip.file(zipPath);

      if (!archivedFile) {
        skipped++;
        continue;
      }

      const data = await archivedFile.async('uint8array');

      await this.db.files.put({
        id: item.id,
        filename: item.filename,
        mimeType: item.mimeType,
        size: item.size,
        eventId: item.eventId,
        fileType: item.fileType,
        data,
        createdAt: new Date(item.createdAt),
      });

      imported++;
    }

    return {
      imported,
      skipped,
      total: manifest.files.length,
    };
  }
}
